import { ref } from "vue";

import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import { collectReportParityMetrics } from "./parityMetrics";
import type { ScenarioContext, SizingMetric } from "./types";

const asRecord = <T = unknown>(value: unknown): Record<string, T> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, T>)
    : {};

const buildYToolAwareEquipment = (
  equipment: Record<string, unknown>
): Record<string, unknown> => {
  const upholeBypassOrder = Array.isArray(
    equipment.uphole_y_tool_bypass_tubing_order
  )
    ? equipment.uphole_y_tool_bypass_tubing_order
    : [];
  const upholeEquipmentOrder = Array.isArray(
    equipment.uphole_y_tool_equipment_tubing_order
  )
    ? equipment.uphole_y_tool_equipment_tubing_order
    : [];
  const downholeBypassOrder = Array.isArray(
    equipment.downhole_y_tool_bypass_tubing_order
  )
    ? equipment.downhole_y_tool_bypass_tubing_order
    : [];
  const downholeEquipmentOrder = Array.isArray(
    equipment.downhole_y_tool_equipment_tubing_order
  )
    ? equipment.downhole_y_tool_equipment_tubing_order
    : [];
  if (!equipment.use_downhole_y_tool) {
    return equipment;
  }
  return {
    ...equipment,
    use_uphole_y_tool: Boolean(
      equipment.use_uphole_y_tool ||
        downholeBypassOrder.length > 0 ||
        downholeEquipmentOrder.length > 0
    ),
    uphole_y_tool_bypass_tubing_order: [
      ...upholeBypassOrder,
      ...downholeBypassOrder
    ],
    uphole_y_tool_equipment_tubing_order: [
      ...upholeEquipmentOrder,
      ...downholeEquipmentOrder
    ]
  };
};

const buildDestructuredScenario = (
  context: ScenarioContext
): DestructuredScenario => {
  const equipment = buildYToolAwareEquipment(asRecord(context.equipment));
  return {
    scenario: ref(context.scenario),
    correlations: ref(context.correlations),
    equipment: ref(equipment),
    parameters: ref(context.parameters),
    wellProperties: ref(context.wellProperties),
    deviationSurvey: ref(context.deviationSurvey),
    intakes: ref(asRecord(context.intakes)),
    pumps: ref(asRecord(context.pumps)),
    housings: ref(asRecord(context.housings)),
    cables: ref(asRecord(context.cables)),
    seals: ref(asRecord(context.seals)),
    yToolBypassTubings: ref({
      ...asRecord(context.yToolBypassTubings),
      ...asRecord(context.downholeYToolBypassTubings)
    }),
    yToolEquipmentTubings: ref({
      ...asRecord(context.yToolEquipmentTubings),
      ...asRecord(context.downholeYToolEquipmentTubings)
    })
  } as unknown as DestructuredScenario;
};

export const collectBrowserSizingParityMetrics = (
  context: ScenarioContext
): Record<string, SizingMetric> => {
  const calculations = useScenarioCalculations(
    buildDestructuredScenario(context)
  );
  return collectReportParityMetrics(calculations);
};
